import React, {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import PhoneInput, {parsePhoneNumber, Country} from "react-phone-number-input";

import {css} from "/src/utils/utils";
import {InputStatus} from "/src/types";
import Check from "/src/svg/check.svg";
import ErrorMessage from "../error-message/ErrorMessage";
import FormError from "/src/svg/form-error.svg";
import GDPRContext from "/src/contexts/GDPRContext";
import Icon from "../../icon/Icon";
import Label from "../label/Label";

import "react-phone-number-input/style.css";
import * as styles from "./legacy-phone.module.scss";

interface Props {
  darkMode?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  label: string;
  onChange: (value: string) => unknown;
  placeholder?: string;
  required?: boolean;
  status?: InputStatus;
  value?: string;
  name: string;
}

export default function LegacyPhone(props: Props): ReactElement {
  const {
    darkMode = false,
    disabled = false,
    errorMessage,
    label,
    onChange,
    placeholder = "",
    required = false,
    status,
    value = "",
    name,
  } = props;

  const isError = status === "error";
  const isSuccess = status === "success";
  const {countryCode} = useContext(GDPRContext);

  const [phoneNo, setPhoneNo] = useState<string>("");
  const [countryCodeValue, setCountryCodeValue] = useState<string>("");
  const [initialCountryCodeValue, setInitialCountryCodeValue] =
    useState<string>("");

  const pickCountry = useCallback((): string => {
    if (countryCode != null) {
      return countryCode;
    }

    return "US";
  }, [countryCode]);

  React.useEffect(() => {
    if (initialCountryCodeValue !== "") {
      return;
    }

    // if there is initial phone value, we need to grab the country code
    const destructureInitialValue =
      value != null ? parsePhoneNumber(value) : null;

    setPhoneNo(
      destructureInitialValue != null
        ? destructureInitialValue.nationalNumber
        : ""
    );

    let initialCountryCode: string =
      destructureInitialValue != null ? destructureInitialValue.country! : "";

    if (initialCountryCode === "") {
      initialCountryCode = pickCountry();
    }

    setInitialCountryCodeValue(initialCountryCode);
  }, [value, pickCountry, initialCountryCodeValue]);

  useEffect(() => {
    setCountryCodeValue(initialCountryCodeValue || "US");
  }, [initialCountryCodeValue]);

  function onPhoneInputChange(val: string): void {
    setPhoneNo(val);

    if (onChange != null) {
      onChange(val || "");
    }
  }

  function onCountryCodeChange(val: Country): void {
    setCountryCodeValue(val);
  }

  return (
    <div
      className={css(
        isError ? styles.legacyPhoneError : "",
        isSuccess ? styles.legacyPhoneSuccess : "",
        darkMode ? styles.legacyPhoneDark : ""
      )}
    >
      <input type="hidden" name={name} value={phoneNo || ""} />
      <input type="hidden" name="country_code" value={countryCodeValue || ""} />

      <Label
        darkMode={darkMode}
        disabled={disabled}
        label={label}
        required={required}
      />

      <div className={styles.legacyPhone__wrapper}>
        <PhoneInput
          className={styles.legacyPhone__component}
          defaultCountry={pickCountry() as Country}
          disabled={disabled}
          name={`${name}-phone-input`}
          onChange={onPhoneInputChange}
          onCountryChange={onCountryCodeChange}
          placeholder={placeholder}
          required={required}
          status={status}
        />

        {(isError || isSuccess) && (
          <Icon
            role="presentation"
            viewBox="0 0 28 28"
            classes={styles.legacyPhone__icon}
            svg={isSuccess ? Check : FormError}
          />
        )}
      </div>

      <ErrorMessage message={errorMessage} visible={isError} />
    </div>
  );
}
